import { Component, ErrorInfo, ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { clearStaleBuildReloadFlag, isModuleLoadError, recoverFromStaleBuild } from "@/utils/retryImport";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
    
    // Check if this is a module loading error
    const isModuleError = isModuleLoadError(error);
    
    if (isModuleError) {
      console.error("Module loading error detected. User should clear cache and reload.");
    }
  }

  public render() {
    if (this.state.hasError) {
      const isModuleError = isModuleLoadError(this.state.error);
      
      return (
        <div className="min-h-screen flex items-center justify-center px-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <CardTitle>
                {isModuleError ? "Module Loading Error" : "Something went wrong"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                {isModuleError 
                  ? "Failed to load application resources. This may be due to a connection issue or outdated cache."
                  : "An unexpected error occurred. Please try refreshing the page or contact support if the problem persists."
                }
              </p>
              {isModuleError && (
                <div className="rounded-md bg-warning/10 border border-warning/20 p-3 text-xs text-muted-foreground">
                  <p className="font-semibold mb-2">Try these steps:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Hard refresh (Ctrl+Shift+R or Cmd+Shift+R)</li>
                    <li>Clear browser cache</li>
                    <li>Try a different browser or incognito mode</li>
                  </ol>
                </div>
              )}
              {this.state.error && (
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer">Error details</summary>
                  <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                    {this.state.error.message}
                  </pre>
                </details>
              )}
              <Button 
                onClick={() => {
                  // User-initiated recovery: drop caches and fetch fresh assets.
                  clearStaleBuildReloadFlag();
                  void recoverFromStaleBuild();
                }} 
                className="w-full"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                {isModuleError ? "Clear Cache & Reload" : "Refresh Page"}
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}